<template lang="pug">
AppDialog.activities-benchmarks-dialog(
  :show="show"
  size="medium"
  append-to-body
  is-dismissable
  title="Activity in Hubstaff"
  :loading="loading"
  @close="onClose"
)
  .benchmarks-content
    ul.benchmarks-tabs
      li.benchmarks-tab(
        v-for="(tab, index) in tabs"
        :key="tab.key"
        :class="{ active: index === activeTab, hidden: tabsData[tab.key] && tabsData[tab.key].hidden }"
      )
        button(
          type="button"
          @click="changeTab(index)"
        ) {{ tab.label }}

        template(v-if="isSmallScreen")
          .benchmarks-tab-content(v-show="index === activeTab && !loading")
            component(
              :is="tab.component"
              :tab-data="tabsData[tab.key]"
            )

    .benchmarks-tab-content(v-if="!isSmallScreen")
      component(
        :is="tab.component"
        v-for="(tab, index) in tabs"
        v-show="index === activeTab && !loading"
        :key="tab.key"
        :tab-data="tabsData[tab.key]"
      )
  template(slot="footer")
    AppButton(@click="onClose") Close
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import isMobile from '~/mixins/isMobile';
import { isNil } from 'lodash-es';

import ActivitiesBenchmarksDialogBasics from './ActivitiesBenchmarksDialogBasics.vue';
import ActivitiesBenchmarksDialogTimers from './ActivitiesBenchmarksDialogTimers.vue';
import ActivitiesBenchmarksDialogHow from './ActivitiesBenchmarksDialogHow.vue';
import ActivitiesBenchmarksDialogJobRoles from './ActivitiesBenchmarksDialogJobRoles.vue';
import ActivitiesBenchmarksDialogOrganization from './ActivitiesBenchmarksDialogOrganization.vue';

export default {
  name: 'ActivitiesBenchmarksDialog',
  components: {
    ActivitiesBenchmarksDialogBasics,
    ActivitiesBenchmarksDialogTimers,
    ActivitiesBenchmarksDialogHow,
    ActivitiesBenchmarksDialogJobRoles,
    ActivitiesBenchmarksDialogOrganization,
  },
  mixins: [isMobile],
  props: {
    // Delegated to AppDialog
    show: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    tabsData: {
      // Showcase the expected final shape of the data.
      // But also to make sure that the prop validation
      // from the tab components do not fail.
      organization_benchmarks: {
        hidden: true,
        industry: {},
        activity_average: null,
        activity_average_industry_diff: null,
      },
      basics: {
        organization_members_url: null,
      },
    },
    loading: false,
    activeTab: 0,
  }),
  computed: {
    ...mapGetters(['hasSeenBenchmarksDialog']),
    tabs() {
      return [
        // `key` will be used later on to try and map the necessary "component" data in the
        // object `data` above; so that we can simply do: `data[key]` and pass it down.
        { key: 'basics', label: 'The basics', component: 'ActivitiesBenchmarksDialogBasics' },
        { key: 'timers', label: 'Timers', component: 'ActivitiesBenchmarksDialogTimers' },
        { key: 'how_it_works', label: 'How it works', component: 'ActivitiesBenchmarksDialogHow' },
        // TBD:
        // { key: 'activity_job_roles', label: 'Activity levels by job roles', component: 'ActivitiesBenchmarksDialogJobRoles' },
        {
          key: 'organization_benchmarks',
          label: 'Activity in your organization',
          component: 'ActivitiesBenchmarksDialogOrganization',
        },
      ];
    },
  },
  watch: {
    // Fire a side effect based on the `show` prop; we want to load the data upon
    // the user opening the dialog and thus always showing the latest data available.
    async show(showing) {
      if (showing) {
        const start = new Date().getTime();

        this.loading = true;

        // Load the necessary data
        const success = await this.loadData();
        // Reset the `activeTab` to the correct index
        const { activity_average: activityAverage } = this.tabsData.organization_benchmarks;

        // Check if we have data to showcase, if so set the tab and remove the loading
        this.activeTab = this.hasSeenBenchmarksDialog && activityAverage ? 3 : 0;

        // Calculate the time we took to load the dialog data
        // if less than 400 just wait until then to not glitch
        // the UI. Otherwise, immediately remove the loading.
        const end = new Date().getTime();
        const diff = end - start;
        const waitFor = Math.max(0, 400 - diff);

        setTimeout(() => {
          this.loading = false;
        }, waitFor);

        // If data loaded successfully and it's the first time the user opened the dialog
        if (success && !this.hasSeenBenchmarksDialog) {
          // Mark the dialog as seen, we don't show a toaster if something fails,
          // as is more of an internal thing to mark this as "seen" and thus the user
          // should not care about it.
          await this.userHasSeenDialog();
        }
      }
    },
  },
  methods: {
    ...mapActions(['userHasSeenDialog', 'loadBenchmarksDialog']),
    onClose() {
      this.$emit('close');
    },
    changeTab(tabIndex) {
      this.activeTab = tabIndex;
    },
    async loadData() {
      const response = await this.loadBenchmarksDialog();

      if (!response) {
        this.$appMessage('Unexpected error ocurred. Please refresh the page.');
      }

      const { activity_average: activityAverage } = response.organization_benchmarks;

      response.organization_benchmarks.hidden = isNil(activityAverage);

      this.tabsData = response;

      return !!response;
    },
  },
};
</script>
